import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';

interface TaskManagerModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (formData: any) => Promise<void>;
  task?: any;
  employees: any[];
  isEditing?: boolean;
}

const CATEGORIES = [
  'Design', 'Printing', 'Finishing', 'Delivery', 'Customer Support',
  'Inventory', 'Accounts', 'Admin', 'Other'
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: '#10b981' },
  { value: 'medium', label: 'Medium', color: '#f59e0b' },
  { value: 'high', label: 'High', color: '#f97316' },
  { value: 'urgent', label: 'Urgent', color: '#ef4444' },
];

const STATUS_OPTIONS = [
  { value: 'todo', label: 'To Do' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'done', label: 'Done' },
  { value: 'cancelled', label: 'Cancelled' },
];

const TaskManagerModal: React.FC<TaskManagerModalProps> = ({
  show,
  onHide,
  onSubmit,
  task,
  employees,
  isEditing = false,
}) => {
  const [form, setForm] = useState({
    title: '',
    description: '',
    assignedTo: '',
    priority: 'medium',
    status: 'todo',
    dueDate: '',
    category: '',
    notes: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (task && isEditing) {
      setForm({
        title: task.title || '',
        description: task.description || '',
        assignedTo: task.assignedTo || '',
        priority: task.priority || 'medium',
        status: task.status || 'todo',
        dueDate: task.dueDate || '',
        category: task.category || '',
        notes: task.notes || '',
      });
    } else {
      setForm({
        title: '',
        description: '',
        assignedTo: '',
        priority: 'medium',
        status: 'todo',
        dueDate: '',
        category: '',
        notes: '',
      });
    }
    setError('');
  }, [task, isEditing, show]);

  const handleChange = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.title.trim()) return setError('Task title is required.');
    if (!form.assignedTo) return setError('Please select an employee to assign this task.');
    setSubmitting(true);
    try {
      await onSubmit(form);
      onHide();
    } catch (err: any) {
      setError(err.message || 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedPriority = PRIORITY_OPTIONS.find(p => p.value === form.priority);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered className="task-modal">
      <Modal.Header
        closeButton
        style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          borderBottom: 'none',
          padding: '1.25rem 1.5rem',
        }}
      >
        <Modal.Title className="text-white fw-bold d-flex align-items-center gap-2">
          <i className={`fas ${isEditing ? 'fa-edit' : 'fa-plus-circle'}`} />
          {isEditing ? 'Edit Task' : 'Create New Task'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: '#f8fafc', padding: '1.75rem' }}>
        {error && (
          <Alert variant="danger" className="py-2 px-3 rounded-3 mb-3" style={{ fontSize: '0.88rem' }}>
            <i className="fas fa-exclamation-circle me-2" />
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit} id="task-form">
          {/* Title */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold text-dark" style={{ fontSize: '0.875rem' }}>
              Task Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter task title..."
              value={form.title}
              onChange={e => handleChange('title', e.target.value)}
              className="rounded-3"
              style={{ border: '1.5px solid #e2e8f0', padding: '0.6rem 0.875rem' }}
            />
          </Form.Group>

          {/* Description */}
          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold text-dark" style={{ fontSize: '0.875rem' }}>
              Description
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Describe the task in detail..."
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              className="rounded-3"
              style={{ border: '1.5px solid #e2e8f0', padding: '0.6rem 0.875rem', resize: 'vertical' }}
            />
          </Form.Group>

          <Row className="g-3 mb-3">
            {/* Assign To */}
            <Col md={6}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: '0.875rem' }}>
                Assign To <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={form.assignedTo}
                onChange={e => handleChange('assignedTo', e.target.value)}
                className="rounded-3"
                style={{ border: '1.5px solid #e2e8f0', padding: '0.6rem 0.875rem' }}
              >
                <option value="">Select employee...</option>
                {employees.map((emp: any) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.username} ({emp.role})
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Category */}
            <Col md={6}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: '0.875rem' }}>
                Category
              </Form.Label>
              <Form.Select
                value={form.category}
                onChange={e => handleChange('category', e.target.value)}
                className="rounded-3"
                style={{ border: '1.5px solid #e2e8f0', padding: '0.6rem 0.875rem' }}
              >
                <option value="">Select category...</option>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            {/* Priority */}
            <Col md={4}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: '0.875rem' }}>
                Priority
              </Form.Label>
              <div className="d-flex gap-2 flex-wrap mt-1">
                {PRIORITY_OPTIONS.map(p => (
                  <button
                    key={p.value}
                    type="button"
                    onClick={() => handleChange('priority', p.value)}
                    style={{
                      border: `2px solid ${p.color}`,
                      background: form.priority === p.value ? p.color : 'transparent',
                      color: form.priority === p.value ? '#fff' : p.color,
                      borderRadius: '20px',
                      padding: '3px 12px',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </Col>

            {/* Status (only when editing) */}
            {isEditing && (
              <Col md={4}>
                <Form.Label className="fw-semibold text-dark" style={{ fontSize: '0.875rem' }}>
                  Status
                </Form.Label>
                <Form.Select
                  value={form.status}
                  onChange={e => handleChange('status', e.target.value)}
                  className="rounded-3"
                  style={{ border: '1.5px solid #e2e8f0', padding: '0.6rem 0.875rem' }}
                >
                  {STATUS_OPTIONS.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </Form.Select>
              </Col>
            )}

            {/* Due Date */}
            <Col md={isEditing ? 4 : 8}>
              <Form.Label className="fw-semibold text-dark" style={{ fontSize: '0.875rem' }}>
                Due Date
              </Form.Label>
              <Form.Control
                type="date"
                value={form.dueDate}
                onChange={e => handleChange('dueDate', e.target.value)}
                className="rounded-3"
                style={{ border: '1.5px solid #e2e8f0', padding: '0.6rem 0.875rem' }}
              />
            </Col>
          </Row>

          {/* Notes */}
          <Form.Group className="mb-2">
            <Form.Label className="fw-semibold text-dark" style={{ fontSize: '0.875rem' }}>
              Internal Notes
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Add any internal notes..."
              value={form.notes}
              onChange={e => handleChange('notes', e.target.value)}
              className="rounded-3"
              style={{ border: '1.5px solid #e2e8f0', padding: '0.6rem 0.875rem', resize: 'vertical' }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: '1rem 1.5rem' }}>
        <Button
          variant="light"
          onClick={onHide}
          disabled={submitting}
          className="rounded-3 px-4"
        >
          Cancel
        </Button>
        <Button
          type="submit"
          form="task-form"
          disabled={submitting}
          style={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            border: 'none',
            borderRadius: '10px',
            padding: '0.5rem 1.5rem',
            fontWeight: 600,
          }}
        >
          {submitting ? (
            <><i className="fas fa-spinner fa-spin me-2" />{isEditing ? 'Saving...' : 'Creating...'}</>
          ) : (
            <><i className={`fas ${isEditing ? 'fa-save' : 'fa-plus'} me-2`} />{isEditing ? 'Save Changes' : 'Create Task'}</>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TaskManagerModal;
